import {
  AfterViewInit,
  Component,
  ElementRef,
  ViewChild,
  OnDestroy,
  OnInit,
} from '@angular/core';
import * as echarts from 'echarts/core';
import { BarChart, PieChart } from 'echarts/charts';
import {
  DatasetComponent,
  GraphicComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  TransformComponent,
} from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';

import {
  COMPANY_KIND_LABELS,
  StudiesService,
} from '../services/studies.service';
import { LogsService } from '../services/logs.service';
import { AlertType } from '../components/alert/alert.component';

interface DashboardStudy {
  isApproved: boolean | null;
  isPreApproved: boolean;
  executive?: {
    firstName?: string;
    lastName?: string;
    username?: string;
  };
  companyKind?: number | string;
  lastDispatch?: unknown;
}

interface StatusCounts {
  approved: number;
  rejected: number;
  noAutomaticApproval: number;
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements AfterViewInit, OnInit, OnDestroy {
  private readonly statusColors = ['#52B788', '#E56B6F', '#E9C46A'];
  private chartByUser?: echarts.ECharts;
  private chartByCompanyKind?: echarts.ECharts;
  private chartByDispatchStatus?: echarts.ECharts;
  private resizeObserver?: ResizeObserver;
  private viewInitialized = false;

  @ViewChild('allChartContainer')
  allChartContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('chartByUserContainer')
  chartByUserContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('chartByCompanyKindContainer')
  chartByCompanyKindContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('chartByDispatchStatusContainer')
  chartByDispatchStatusContainer!: ElementRef<HTMLDivElement>;
  ALERT = AlertType;
  chart?: echarts.ECharts;
  loading = false;
  hasLoaded = false;
  message: string | null = null;
  selectedDate: string;
  studies: DashboardStudy[] = [];

  get hasStudies(): boolean {
    return this.studies.length > 0;
  }

  constructor(
    private studiesService: StudiesService,
    private logs: LogsService,
  ) {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    this.selectedDate = `${now.getFullYear()}-${month}`;
    echarts.use([
      BarChart,
      GraphicComponent,
      LegendComponent,
      PieChart,
      TitleComponent,
      TooltipComponent,
      GridComponent,
      DatasetComponent,
      TransformComponent,
      LabelLayout,
      UniversalTransition,
      CanvasRenderer,
    ]);
  }

  async ngOnInit() {
    this.logs.enviarLog('visita', 'panel-de-control');
    this.loadStudies();
  }

  ngOnDestroy(): void {
    this.disposeCharts();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  async loadStudies(month?: number, year?: number) {
    if (this.loading) {
      return;
    }

    this.message = null;
    this.loading = true;
    const data = await this.studiesService.getMonthlySummary(month, year);

    this.studies = Array.isArray(data) ? data : data?.studies || [];

    if (data === null || data === undefined) {
      this.message = 'Error al cargar tu historial';
    }

    this.loading = false;
    this.hasLoaded = true;

    setTimeout(() => this.syncCharts(), 0);
  }

  getValue(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }

  async onMonthChange(event: Event) {
    const selectedDate = this.getValue(event);

    if (!selectedDate) {
      return;
    }

    this.selectedDate = selectedDate;
    const [year, month] = selectedDate.split('-').map(Number);

    await this.loadStudies(month, year);
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.syncCharts();

    this.resizeObserver = new ResizeObserver(() => {
      this.resizeCharts();
    });

    this.resizeObserver.observe(this.allChartContainer.nativeElement);
    this.resizeObserver.observe(this.chartByUserContainer.nativeElement);
    this.resizeObserver.observe(this.chartByCompanyKindContainer.nativeElement);
    this.resizeObserver.observe(
      this.chartByDispatchStatusContainer.nativeElement,
    );
  }

  private syncCharts() {
    if (!this.viewInitialized) {
      return;
    }

    if (!this.hasStudies) {
      this.disposeCharts();
      return;
    }

    this.showAllChart();
    this.showChartByUser();
    this.showChartByCompanyKind();
    this.showChartByDispatchStatus();
  }

  private disposeCharts() {
    if (this.chart) {
      this.chart.dispose();
      this.chart = undefined;
    }

    if (this.chartByUser) {
      this.chartByUser.dispose();
      this.chartByUser = undefined;
    }

    if (this.chartByCompanyKind) {
      this.chartByCompanyKind.dispose();
      this.chartByCompanyKind = undefined;
    }

    if (this.chartByDispatchStatus) {
      this.chartByDispatchStatus.dispose();
      this.chartByDispatchStatus = undefined;
    }
  }

  private resizeCharts() {
    this.chart?.resize();
    this.chartByUser?.resize();
    this.chartByCompanyKind?.resize();
    this.chartByDispatchStatus?.resize();
  }

  private createEmptyStatusCounts(): StatusCounts {
    return {
      approved: 0,
      rejected: 0,
      noAutomaticApproval: 0,
    };
  }

  private addStudyToStatusCounts(counts: StatusCounts, study: DashboardStudy) {
    if (study.isApproved === true) {
      counts.approved += 1;
    } else if (study.isApproved === false) {
      counts.rejected += 1;
    } else if (study.isApproved === null && study.isPreApproved === false) {
      counts.noAutomaticApproval += 1;
    }
  }

  private getStatusCounts(studies: DashboardStudy[]): StatusCounts {
    const counts = this.createEmptyStatusCounts();

    studies.forEach((study) => this.addStudyToStatusCounts(counts, study));

    return counts;
  }

  private getTotalFromStatusCounts(counts: StatusCounts): number {
    return counts.approved + counts.rejected + counts.noAutomaticApproval;
  }

  private groupStudiesBy(
    studies: DashboardStudy[],
    keySelector: (study: DashboardStudy) => string,
  ): Record<string, StatusCounts> {
    return studies.reduce((acc: Record<string, StatusCounts>, study) => {
      const key = keySelector(study);

      if (!acc[key]) {
        acc[key] = this.createEmptyStatusCounts();
      }

      this.addStudyToStatusCounts(acc[key], study);

      return acc;
    }, {});
  }

  private getOrCreateChart(
    chart: echarts.ECharts | undefined,
    container: ElementRef<HTMLDivElement>,
  ): echarts.ECharts {
    return chart || echarts.init(container.nativeElement);
  }

  showAllChart() {
    const studies = this.studies || [];
    const counts = this.getStatusCounts(studies);
    const totalStudies = this.getTotalFromStatusCounts(counts);

    this.chart = this.getOrCreateChart(this.chart, this.allChartContainer);

    this.chart.setOption(
      {
        color: this.statusColors,
        title: {
          text: 'Distribución total de Consultas',
        },
        graphic: {
          type: 'text',
          left: 'center',
          top: 'middle',
          style: {
            text: totalStudies === 0 ? '' : `Total\n${totalStudies}`,
            textAlign: 'center',
            fill: '#666',
            fontSize: 14,
            fontWeight: 600,
          },
        },
        tooltip: {
          trigger: 'item',
          formatter: (params: {
            name: string;
            value: number;
            percent: number;
          }) => `${params.name}<br/>Cantidad: ${params.value}`,
        },
        legend: {
          orient: 'vertical',
          left: 'left',
          data: ['Aprobados', 'Rechazados', 'Sin aprobación automática'],
        },
        series: [
          {
            type: 'pie',
            radius: ['40%', '70%'],
            emphasis: {
              scale: false,
              itemStyle: {
                opacity: 1,
              },
            },
            label: {
              show: true,
              position: 'inside',
              formatter: (params) =>
                params.value === 0 ? '' : `${params.percent}%`,
            },
            data: [
              { value: counts.approved, name: 'Aprobados' },
              { value: counts.rejected, name: 'Rechazados' },
              {
                value: counts.noAutomaticApproval,
                name: 'Sin aprobación automática',
              },
            ],
          },
        ],
      },
      true,
    );
  }

  showChartByUser() {
    const studies = this.studies || [];
    const byUser = this.groupStudiesBy(studies, (study) => {
      return (
        (study?.executive?.firstName &&
          study?.executive?.lastName &&
          `${study.executive.firstName} ${study.executive.lastName}`) ||
        study?.executive?.username ||
        'Sin usuario'
      );
    });

    const sortedUsers = Object.keys(byUser)
      .map((name) => ({
        name,
        approved: byUser[name].approved,
        rejected: byUser[name].rejected,
        noAutomaticApproval: byUser[name].noAutomaticApproval,
        total:
          byUser[name].approved +
          byUser[name].rejected +
          byUser[name].noAutomaticApproval,
      }))
      .sort((a, b) => b.total - a.total);

    const userLabels = sortedUsers.map((item) => item.name);
    const approvedCounts = sortedUsers.map((item) => item.approved);
    const rejectedCounts = sortedUsers.map((item) => item.rejected);
    const noAutomaticApprovalCounts = sortedUsers.map(
      (item) => item.noAutomaticApproval,
    );
    const totalByUser = sortedUsers.map((item) => item.total);

    this.chartByUser = this.getOrCreateChart(
      this.chartByUser,
      this.chartByUserContainer,
    );

    this.chartByUser.setOption(
      {
        color: this.statusColors,
        title: {
          text: 'Consultas por ejecutivo',
        },
        tooltip: {},
        legend: {
          data: ['Aprobados', 'Rechazados', 'Sin aprobación automática'],
        },
        xAxis: {
          type: 'category',
          data: userLabels,
        },
        yAxis: {
          type: 'value',
        },
        series: [
          {
            type: 'bar',
            stack: 'total',
            name: 'Aprobados',
            label: {
              show: true,
              position: 'inside',
              formatter: ({ value }: { value: number }) =>
                value === 0 ? '' : `${value}`,
            },
            data: approvedCounts,
          },
          {
            type: 'bar',
            stack: 'total',
            name: 'Rechazados',
            label: {
              show: true,
              position: 'inside',
              formatter: ({ value }: { value: number }) =>
                value === 0 ? '' : `${value}`,
            },
            data: rejectedCounts,
          },
          {
            type: 'bar',
            stack: 'total',
            name: 'Sin aprobación automática',
            label: {
              show: true,
              position: 'inside',
              formatter: ({ value }: { value: number }) =>
                value === 0 ? '' : `${value}`,
            },
            data: noAutomaticApprovalCounts,
          },
          {
            type: 'bar',
            stack: 'total',
            name: 'Total',
            itemStyle: {
              color: 'transparent',
            },
            emphasis: {
              disabled: true,
            },
            label: {
              show: true,
              position: 'top',
              formatter: ({ dataIndex }: { dataIndex: number }) => {
                const total = totalByUser[dataIndex] || 0;
                return total === 0 ? '' : `${total}`;
              },
              color: '#555',
              fontWeight: 600,
            },
            data: new Array(userLabels.length).fill(0),
            tooltip: {
              show: false,
            },
          },
        ],
      },
      true,
    );
  }

  showChartByCompanyKind() {
    const studies = this.studies || [];
    const byCompanyKind = this.groupStudiesBy(studies, (study) => {
      const companyKindCode = Number(study?.companyKind);
      const companyKind =
        COMPANY_KIND_LABELS[companyKindCode] ||
        study?.companyKind ||
        'Sin tipo de compañía';

      return String(companyKind);
    });

    const sortedCompanyKinds = Object.keys(byCompanyKind)
      .map((companyKind) => ({
        companyKind,
        approved: byCompanyKind[companyKind].approved,
        rejected: byCompanyKind[companyKind].rejected,
        noAutomaticApproval: byCompanyKind[companyKind].noAutomaticApproval,
        total:
          byCompanyKind[companyKind].approved +
          byCompanyKind[companyKind].rejected +
          byCompanyKind[companyKind].noAutomaticApproval,
      }))
      .sort((a, b) => b.total - a.total);

    const companyKindLabels = sortedCompanyKinds.map(
      (item) => item.companyKind,
    );
    const approvedCounts = sortedCompanyKinds.map((item) => item.approved);
    const rejectedCounts = sortedCompanyKinds.map((item) => item.rejected);
    const noAutomaticApprovalCounts = sortedCompanyKinds.map(
      (item) => item.noAutomaticApproval,
    );
    const totalByCompanyKind = sortedCompanyKinds.map((item) => item.total);

    this.chartByCompanyKind = this.getOrCreateChart(
      this.chartByCompanyKind,
      this.chartByCompanyKindContainer,
    );

    this.chartByCompanyKind.setOption(
      {
        color: this.statusColors,
        title: {
          text: 'Consultas por tipo de compañía',
        },
        tooltip: {},
        legend: {
          data: ['Aprobados', 'Rechazados', 'Sin aprobación automática'],
        },
        xAxis: {
          type: 'category',
          data: companyKindLabels,
        },
        yAxis: {
          type: 'value',
        },
        series: [
          {
            type: 'bar',
            stack: 'total',
            name: 'Aprobados',
            label: {
              show: true,
              position: 'inside',
              formatter: ({ value }: { value: number }) =>
                value === 0 ? '' : `${value}`,
            },
            data: approvedCounts,
          },
          {
            type: 'bar',
            stack: 'total',
            name: 'Rechazados',
            label: {
              show: true,
              position: 'inside',
              formatter: ({ value }: { value: number }) =>
                value === 0 ? '' : `${value}`,
            },
            data: rejectedCounts,
          },
          {
            type: 'bar',
            stack: 'total',
            name: 'Sin aprobación automática',
            label: {
              show: true,
              position: 'inside',
              formatter: ({ value }: { value: number }) =>
                value === 0 ? '' : `${value}`,
            },
            data: noAutomaticApprovalCounts,
          },
          {
            type: 'bar',
            stack: 'total',
            name: 'Total',
            itemStyle: {
              color: 'transparent',
            },
            emphasis: {
              disabled: true,
            },
            label: {
              show: true,
              position: 'top',
              formatter: ({ dataIndex }: { dataIndex: number }) => {
                const total = totalByCompanyKind[dataIndex] || 0;
                return total === 0 ? '' : `${total}`;
              },
              color: '#555',
              fontWeight: 600,
            },
            data: new Array(companyKindLabels.length).fill(0),
            tooltip: {
              show: false,
            },
          },
        ],
      },
      true,
    );
  }

  showChartByDispatchStatus() {
    const studies = this.studies || [];
    const dispatchedStudies = studies.filter(
      (study) => study.lastDispatch !== null,
    );
    const counts = this.getStatusCounts(dispatchedStudies);
    const totalDispatchStudies = this.getTotalFromStatusCounts(counts);

    this.chartByDispatchStatus = this.getOrCreateChart(
      this.chartByDispatchStatus,
      this.chartByDispatchStatusContainer,
    );

    this.chartByDispatchStatus.setOption(
      {
        color: this.statusColors,
        title: {
          text: 'Total de Despacho por Estado de Aprobación',
        },
        graphic: {
          type: 'text',
          left: 'center',
          top: 'middle',
          style: {
            text:
              totalDispatchStudies === 0
                ? ''
                : `Total\n${totalDispatchStudies}`,
            textAlign: 'center',
            fill: '#666',
            fontSize: 14,
            fontWeight: 600,
          },
        },
        tooltip: {
          trigger: 'item',
          formatter: (params: { name: string; value: number }) =>
            `${params.name}<br/>Cantidad: ${params.value}`,
        },
        legend: {
          orient: 'vertical',
          left: 'left',
          data: ['Aprobados', 'Rechazados', 'Pendiente'],
        },
        series: [
          {
            type: 'pie',
            radius: ['40%', '70%'],
            label: {
              show: true,
              position: 'inside',
              formatter: (params) =>
                params.value === 0 ? '' : `${params.percent}%`,
            },
            data: [
              { value: counts.approved, name: 'Aprobados' },
              { value: counts.rejected, name: 'Rechazados' },
              {
                value: counts.noAutomaticApproval,
                name: 'Pendiente',
              },
            ],
          },
        ],
      },
      true,
    );
  }
}
